<template>
  <div>
    <BaseDataTable slim :columns="columns" :entries="entries" data-test="timesheet-form-table">
      <template #thead>
        <tr>
          <th></th>
          <th v-for="date in timesheetFormStore.activeWeek.dates" :key="date">
            <div class="tw-text-sm tw-text-gray-700 tw-w-full tw-text-right">
              <div>{{ `${$t(date.format('DD'))} ${date.format('MMM')}` }}</div>
              <div>{{ $t(date.format('ddd')) }}</div>
            </div>
          </th>
          <th>
            <div class="tw-text-sm tw-text-gray-900 tw-w-full tw-text-right">
              <div>{{ $t('Week') }}</div>
              <div>{{ $t('total') }}</div>
            </div>
          </th>
        </tr>
      </template>
      <!--   Labels for each row which are displayed in the left most column cells of the table   -->
      <template #label="{ row }">
        <!--    Label for timesheet lines    -->
        <template v-if="row.type === EntryType.TIMESHEET_LINE">
          <div
            class="tw-flex tw-justify-between tw-items-center tw-cursor-pointer tw-gap-1"
            tabindex="0"
            role="button"
            @click="timesheetLinesStore.openModalToEditLine(row.entriesKey)"
            @keydown.enter="timesheetLinesStore.openModalToEditLine(row.entriesKey)"
          >
            <div class="tw-line-clamp-3 tw-overflow-hidden tw-break-words">
              {{ row.label }}
            </div>
            <span>
              <WdsTooltip
                v-if="
                  !timesheetLinesStore.timesheetLines.find((line) => line.title === row.entriesKey)
                    ?.isPayable
                "
                :text="$t('Non-payable')"
              >
                <WdsIcon
                  :size="14"
                  weight="thin"
                  :name="WdsIconNameEnum.NON_PAYABLE"
                  class="!tw-inline-block tw-text-gray-700"
                />
              </WdsTooltip>
            </span>
          </div>
        </template>
        <div
          v-else
          class="tw-text-gray-900"
          :class="{
            'parent-row-label': ['duration', 'comments'].includes(row.entriesKey),
            'child-row-label': !['duration', 'comments'].includes(row.entriesKey),
            'tw-cursor-pointer': row.entriesKey === 'duration',
          }"
          @click="row.entriesKey === 'duration' && toggleDetailRows()"
        >
          <div class="tw-flex tw-justify-between tw-items-center">
            {{ row.label }}
            <WdsIcon
              v-if="
                timesheetFormStore.useDetailedView &&
                !timesheetFormStore.useDayInputs &&
                row.entriesKey === 'duration'
              "
              name="chevron-up"
              :class="chevronClass"
              :size="16"
            />
          </div>
        </div>
      </template>
      <!--   Content of right-most column showing the sum for the relevant row   -->
      <template #week-total="{ row }">
        <!--    Only for rows which are timesheet lines OR the total duration    -->
        <div
          v-if="row.entriesKey === 'duration' || row.type === EntryType.TIMESHEET_LINE"
          class="tw-text-lg tw-h-full tw-flex tw-items-center tw-justify-end"
        >
          {{
            timesheetFormStore.sumsForActiveWeek[
              row.type === EntryType.TIMESHEET_LINE ? row.entriesKey : 'total'
            ]
          }}
          {{
            timesheetFormStore.useDayInputs
              ? $t('d (day abbreviation)')
              : $t('h (hour abbreviation)')
          }}
        </div>
      </template>
      <!--   Slot content for the cells in each day of the week   -->
      <template #day-column="{ col, row }">
        <div class="tw-w-full">
          <!-- For detailed view w start, end, and break time -->
          <ComputedTotal
            v-if="row.type === EntryType.COMPUTED_TOTAL"
            :disabled="timesheetFormStore.isDateDisabled(col.dateString)"
            :date-string="col.dateString"
            :start-time="timesheetFormStore.tableDateEntries[col.dateString].startTime"
            :end-time="timesheetFormStore.tableDateEntries[col.dateString].endTime"
            :break-hours="timesheetFormStore.tableDateEntries[col.dateString].break"
          />
          <Time v-if="['startTime', 'endTime'].includes(row.entriesKey)" :row="row" :col="col" />
          <Breaktime v-if="row.entriesKey === 'break'" :row="row" :col="col" />
          <!-- Total time reported for each day, on non detailed view -->
          <TotalDuration
            v-if="row.entriesKey === 'duration' && row.type !== EntryType.COMPUTED_TOTAL"
            :row="row"
            :col="col"
          />
          <!-- Timesheet line  rows-->
          <Line v-if="row.type === EntryType.TIMESHEET_LINE" :row="row" :col="col" />
          <!-- Comments row -->
          <Comment v-if="row.entriesKey === 'comments'" :row="row" :col="col" />
        </div>
      </template>
    </BaseDataTable>
    <WdsInputArea
      v-if="timesheetFormStore.showCommentInput.show"
      v-model="timesheetFormStore.comment"
      maxlength="255"
      show-character-count
    ></WdsInputArea>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { WdsIcon, WdsInputArea, WdsIconNameEnum, WdsTooltip } from '@worksome/wds-components'
import { useTranslation } from '@/composables/translation'
import BaseDataTable from '@/components/BaseDataTable/BaseDataTable.vue'
import { useTimesheetFormStore } from '@/components/Timesheet/store/useTimesheetFormStore'
import ComputedTotal from '@/components/Timesheet/Cells/ComputedTotal.vue'
import { useTimesheetLinesStore } from '@/components/Timesheet/store/useTimesheetLinesStore'
import Time from '@/components/Timesheet/Cells/Time.vue'
import Breaktime from '@/components/Timesheet/Cells/Breaktime.vue'
import TotalDuration from '@/components/Timesheet/Cells/TotalDuration.vue'
import Line from '@/components/Timesheet/Cells/Line.vue'
import Comment from '@/components/Timesheet/Cells/Comment.vue'

const { $t } = useTranslation()
const timesheetFormStore = useTimesheetFormStore()
const timesheetLinesStore = useTimesheetLinesStore()

const columns = computed(() => [
  // Empty col header to account for left-side labels
  {
    key: 'label',
    slot: 'label',
    title: '',
  },
  // Get column for each day in the active week
  ...(timesheetFormStore.activeWeek?.dates.map((date) => ({
    key: date.format('ddd').toLowerCase(),
    dateString: date.format('YYYY-MM-DD'),
    day: $t(date.format('ddd')),
    dateAndMonth: `${$t(date.format('DD'))} ${date.format('MMM')}`,
    slot: 'day-column',
  })) || []),
  // Weekly total
  {
    key: 'week-total',
    slot: 'week-total',
    title: 'Week total',
  },
])

const areDetailRowsExpanded = ref(true)

enum EntryType {
  NUMBER = 'NUMBER',
  TIME = 'TIME',
  COMPUTED_TOTAL = 'COMPUTED_TOTAL',
  TEXT = 'TEXT',
  TIMESHEET_LINE = 'TIMESHEET_LINE',
}

const entries = computed(() => {
  const returnEntries: { entriesKey: string; label: string; type: EntryType }[] = []

  if (timesheetFormStore.useDetailedView) {
    returnEntries.push({
      entriesKey: 'duration',
      label: `${$t('Day total')} (${timesheetFormStore.useDayInputs ? $t('days') : $t('hours')})`,
      type: EntryType.COMPUTED_TOTAL,
    })
    if (areDetailRowsExpanded.value) {
      returnEntries.push({
        entriesKey: 'startTime',
        label: $t('Start time'),
        type: EntryType.TIME,
      })
      returnEntries.push({
        entriesKey: 'break',
        label: $t('Break (hours)'),
        type: EntryType.NUMBER,
      })
      returnEntries.push({
        entriesKey: 'endTime',
        label: $t('End time'),
        type: EntryType.TIME,
      })
    }
  } else {
    returnEntries.push({
      entriesKey: 'duration',
      label: `${$t('Day total')} (${timesheetFormStore.useDayInputs ? $t('days') : $t('hours')})`,
      type: EntryType.NUMBER,
    })
  }

  timesheetLinesStore.timesheetLines.forEach((line) => {
    returnEntries.push({
      entriesKey: line.title,
      label: line.title,
      type: EntryType.TIMESHEET_LINE,
    })
  })

  returnEntries.push({
    entriesKey: 'comments',
    label: $t('Comment'),
    type: EntryType.TEXT,
  })

  return returnEntries
})

const chevronClass = ref('chevron-down')

const toggleDetailRows = () => {
  areDetailRowsExpanded.value = !areDetailRowsExpanded.value
  chevronClass.value = chevronClass.value === 'chevron-down' ? 'chevron-up' : 'chevron-down'
}
</script>

<style scoped>
@tailwind base;
.parent-row-label {
  @apply tw-text-[16px];
}

.child-row-label {
  @apply tw-ml-2;
  @apply tw-text-base;
}

.chevron-down {
  transform: rotate(0deg);
  transition: transform 200ms linear;
}

.chevron-up {
  transform: rotate(180deg);
  transition: transform 200ms linear;
}

:deep(table .wds-input) {
  @apply tw-text-right;
}
:deep(.wds-input-label) {
  @apply tw-m-0;
}

thead > tr > th {
  @apply tw-bg-true-gray-100;
  @apply tw-text-[13px];
  @apply tw-text-gray-600;
  @apply tw-border-none;
}

:deep(tbody > tr:first-child) {
  @apply tw-bg-true-gray-100;
}
:deep(tbody > tr:first-child td) {
  border-bottom: 2px solid #e9e9ed !important;
}

:deep(.table th:last-child),
:deep(.table td:last-child) {
  border-left-style: solid;
  @apply tw-border-l tw-border-l-gray-100 tw-border-b-0;
}

:deep(.table th:first-child),
:deep(.table td:first-child) {
  border-right-style: solid;
  @apply tw-border-r tw-border-r-gray-100;
}

:deep(tbody:before) {
  content: none !important;
  display: none !important;
}
:deep(td) {
  @apply !tw-px-2;
}
:deep(.table th:first-child) {
  @apply !tw-w-[180px];
  @apply !tw-min-w-[180px];
  @apply !tw-max-w-[180px];
}
:deep(.table th:last-child) {
  @apply !tw-w-[72px];
  @apply !tw-min-w-[72px];
  @apply !tw-max-w-[72px];
}

:deep(input) {
  @apply !tw-w-20;
  @apply !tw-min-w-full;
}
:deep(tbody tr:first-child td:last-child) {
  @apply tw-text-indigo-500;
}
</style>
